import { fg } from '@atlassian/jira-feature-gating';
import type {
	StatusTransition,
	ValidationEntry,
} from '@atlassian/jira-issue-field-status/src/common/types.tsx';

const shouldReplace = (existing: StatusTransition, replacement: StatusTransition): boolean =>
	(!existing.isInitial && replacement.isInitial) ||
	(existing.isDisabled === true && !replacement.isDisabled);

type TransitionWithValidations = StatusTransition & {
	validations?: ValidationEntry[];
};

type ApiResponse = TransitionWithValidations[];

export type TransitionResponse = {
	transitions: StatusTransition[];
	fieldRequiredValidators?: string[];
};

export const transformDataWithValidations = (
	data: ApiResponse,
): StatusTransition[] | TransitionResponse => {
	if (!Array.isArray(data)) {
		return [];
	}

	const transitionMap = new Map<string, StatusTransition>();
	const requiredFields = new Set<string>();

	for (const val of data) {
		const fieldValidators: ValidationEntry['fieldValidations'][] = [];
		// Extract validations if needed (during the same iteration)
		if (requiredFields && val.validations?.length) {
			for (const validation of val.validations) {
				const requiredFieldValidators: ValidationEntry['fieldValidations'] = [];
				for (const fieldValidation of validation.fieldValidations || []) {
					if (fieldValidation.type === 'FIELDREQUIREDVALIDATOR') {
						fg('fix_jracloud_74998_fg') && requiredFieldValidators.push(fieldValidation);
						fieldValidation.fields.forEach((field) => requiredFields.add(field));
					}
				}
				fg('fix_jracloud_74998_fg') && fieldValidators.push(requiredFieldValidators);
			}
		}

		// Process transition (clean object without validations)
		const cleanTransition: StatusTransition = {
			id: val.id,
			isConditional: val.isConditional,
			isGlobal: val.isGlobal,
			isInitial: val.isInitial,
			hasScreen: val.hasScreen,
			isLooped: val.isLooped,
			name: val.name,
			to:
				val.to.translatedName && fg('translated_status_gic')
					? {
							...val.to,
							name: val.to.translatedName,
						}
					: val.to,
			isDisabled: val.isDisabled,
			unavailableReasonText: val.unavailableReasonText,
			statusCategory: val.statusCategory,
			fieldValidators: fg('fix_jracloud_74998_fg') ? fieldValidators : null,
		};

		const existingTransition = transitionMap.get(cleanTransition.to.id);
		if (!existingTransition || shouldReplace(existingTransition, cleanTransition)) {
			transitionMap.set(cleanTransition.to.id, cleanTransition);
		}
	}

	const transitions = Array.from(transitionMap.values());

	return { transitions, fieldRequiredValidators: Array.from(requiredFields) };
};

export const transformData = (data: StatusTransition[]): StatusTransition[] => {
	const transitionMap = data.reduce((acc, val) => {
		if (acc.has(val.to.id) && !shouldReplace(acc.get(val.to.id), val)) {
			return acc;
		}

		const transformedTransition =
			val.to.translatedName && fg('translated_status_gic')
				? {
						...val,
						to: {
							...val.to,
							name: val.to.translatedName,
						},
					}
				: val;

		return acc.set(val.to.id, transformedTransition);
	}, new Map());

	return Array.from(transitionMap.values());
};
